import { useEffect, useState } from "react";
import '../../assets/css/MenuPanel.css';
import { getYearsAndMonthsOfFeature } from '../../static/js/DataRequestFunctions';
import locale from '../locales/MenuPanel.locale';
import HexSizeSwitcher from './HexSizeSwitcher'; // <-- импорт компонента

export default function MenuPanel({
                                      products = [],
                                      currentProductIndex,
                                      setCurrentProductIndex,
                                      year,
                                      setYear,
                                      month,
                                      language,
                                      setMonth,
                                      openLayer,
                                      setOpenLayer,
                                      selectedFeatureId,
                                      setSelectedFeatureId,
                                      selectedLayerId,
                                      setSelectedLayerId,
                                      handleSelectionLayer,
                                      handleSelectionFeature,
                                      selectedObjectValue,
                                      pickedAreaData,
                                      // новые пропсы для размера гексагона
                                      hexSize,
                                      setHexSize,
                                      selectedObjectIds,
                                      isLoading,
                                      clusterData = [],
                                      onClusterSelect,
                                      onClusterFlash,
                                  }) {
    const [activeTab, setActiveTab] = useState("menu");
    const [cityOpen, setCityOpen] = useState(false);
    const [availableYears, setAvailableYears] = useState([]);
    const [availableMonths, setAvailableMonths] = useState([]);
    const [yearsMonthsMap, setYearsMonthsMap] = useState({});
    const [menuOpen, setMenuOpen] = useState(true);
    const [activeClusterIdx, setActiveClusterIdx] = useState(0);

    const t = locale[language];
    const currentProduct = products[currentProductIndex] || {};

    useEffect(() => {
        if (!products.length) return;

        const product = products[currentProductIndex];
        if (!product?.layers?.length) return;

        const stillOnLayer = product.layers.some((l) => String(l.id) === String(selectedLayerId));
        if (stillOnLayer) {
            const layer = product.layers.find((l) => String(l.id) === String(selectedLayerId));
            const stillOnFeature = layer.features?.some((f) => String(f.id) === String(selectedFeatureId));
            if (!stillOnFeature && layer.features?.[0]) {
                setSelectedFeatureId(layer.features[0].id);
            }
            return;
        }

        const firstLayer = product.layers[0];
        setOpenLayer(firstLayer.id);
        setSelectedLayerId(firstLayer.id);

        if (firstLayer.features?.length > 0) {
            const firstFeature = firstLayer.features[0];
            setSelectedFeatureId(firstFeature.id);
        }
    }, [products, currentProductIndex]);

    useEffect(() => {
        if (window.innerWidth < 640) {
            setMenuOpen(false);
        }
    }, []);

    useEffect(() => {
        setActiveClusterIdx(0);
    }, [selectedLayerId, clusterData.length, selectedObjectIds?.length]);

    const productDisplayName = (p, index) =>
        p?.full_name || p?.name || p?.title || `${t.product.title} #${(index ?? 0) + 1}`;
    const normName = (s) => String(s || "").trim().toLowerCase();

    const currentProductName = () => {
        if (!products || products.length === 0) return t.common.loading;
        return productDisplayName(currentProduct, currentProductIndex);
    };

    const currentCity = () => {
        if (!products || products.length === 0) return t.common.notSpecified;
        return currentProduct?.region?.name || t.common.notSpecified;
    };

    const uniqueProductNames = [];
    products.forEach((p, index) => {
        const name = productDisplayName(p, index);
        if (!uniqueProductNames.some((n) => normName(n) === normName(name))) uniqueProductNames.push(name);
    });

    const selectProductByName = (name, preferRegionId = currentProduct?.region?.id) => {
        const sameRegion = products.findIndex((p, i) =>
            normName(productDisplayName(p, i)) === normName(name) && p?.region?.id === preferRegionId
        );
        const any = products.findIndex((p, i) => normName(productDisplayName(p, i)) === normName(name));
        const next = sameRegion >= 0 ? sameRegion : any;
        if (next >= 0) setCurrentProductIndex(next);
    };

    const availableCities = (() => {
        const currentName = currentProductName();
        const seen = new Set();
        const cities = [];
        products.forEach((p, index) => {
            if (normName(productDisplayName(p, index)) !== normName(currentName)) return;
            const id = p?.region?.id;
            const name = p?.region?.name;
            if (id == null || !name || seen.has(id)) return;
            seen.add(id);
            cities.push({ id, name, productIndex: index });
        });
        return cities;
    })();

    const prevProduct = () => {
        if (!uniqueProductNames.length) return;
        const idx = uniqueProductNames.indexOf(currentProductName());
        const nextName = uniqueProductNames[(idx - 1 + uniqueProductNames.length) % uniqueProductNames.length];
        selectProductByName(nextName);
    };

    const nextProduct = () => {
        if (!uniqueProductNames.length) return;
        const idx = uniqueProductNames.indexOf(currentProductName());
        const nextName = uniqueProductNames[(idx + 1) % uniqueProductNames.length];
        selectProductByName(nextName);
    };

    useEffect(() => {
        if (!selectedFeatureId || !currentProduct?.region?.id) return;

        const fetchYearsMonths = async () => {
            const data = await getYearsAndMonthsOfFeature(
                selectedFeatureId,
                currentProduct.region?.id
            );

            const parsedYears = Object.keys(data).map(Number).sort((a,b) => a-b);
            setAvailableYears(parsedYears);

            const map = Object.fromEntries(
                Object.entries(data).map(([y, months]) => [
                    Number(y),
                    months.filter(m => m >= 1 && m <= 12)
                ])
            );
            setYearsMonthsMap(map);

            let nextYear = year;
            if (!nextYear || !map[nextYear]) {
                nextYear = parsedYears[0] ?? null;
                setYear(nextYear);
            }

            if (nextYear && map[nextYear]) {
                setAvailableMonths(map[nextYear]);
                if (!map[nextYear].includes(month)) {
                    setMonth(map[nextYear][0] ?? null);
                }
            }
        };

        fetchYearsMonths();
    }, [selectedFeatureId, currentProduct]);

    useEffect(() => {
        if (year && yearsMonthsMap[year]) {
            setAvailableMonths(yearsMonthsMap[year]);
            if (!yearsMonthsMap[year].includes(month)) setMonth(yearsMonthsMap[year][0]);
        } else {
            setAvailableMonths([]);
            setMonth(null);
        }
    }, [year, yearsMonthsMap]);

    function declOfNum(n, titles) {
        return titles[
            n % 10 === 1 && n % 100 !== 11 ? 0 :
                n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 1 : 2
            ];
    }

    function formatValue(value) {
        if (value == null) return '—';
        if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
        if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
        return String(Math.round(value * 100) / 100);
    }

    return (
        <>
            {!menuOpen && (
                <div
                    className="menu-toggle-btn"
                    onClick={() => setMenuOpen(true)}
                    title={t.menuField.toOpen}
                >
                    ☰
                </div>
            )}

            <div className={`preferences-panel ${menuOpen ? "" : "collapsed"}`}>
                <div className="tab-buttons">
                    <div
                        className={`tab-button ${activeTab === "menu" ? "active" : ""}`}
                        onClick={() => setActiveTab("menu")}
                    >
                        {t.menuField.title}
                    </div>

                    <div
                        className={`tab-button ${activeTab === "point" ? "active" : ""}`}
                        onClick={() => setActiveTab("point")}
                    >
                        {t.menuField.fields.selectedPoint}
                    </div>

                    <div
                        className="tab-button"
                        onClick={() => setMenuOpen(false)}
                        style={{ maxWidth: 48 }}
                        title={t.common.collapse}
                    >
                        ✕
                    </div>
                </div>

                {/* Основное меню */}
                <div className={`tab-content ${activeTab === "menu" ? "active" : ""}`}>
                    {/* Выбор продукта */}
                    <div className="section-title">{t.product.title}</div>
                    <div className="product-switch">
                        <button
                            className="product-btn"
                            onClick={prevProduct}
                            title={t.product.previous}
                            disabled={uniqueProductNames.length <= 1}
                        >
                            &#9664;
                        </button>
                        <div className="product-label">{currentProductName()}</div>
                        <button
                            className="product-btn"
                            onClick={nextProduct}
                            title={t.product.next}
                            disabled={uniqueProductNames.length <= 1}
                        >
                            &#9654;
                        </button>
                    </div>

                    {/* Выбор города */}
                    <div className="section-title">{t.city.title}</div>
                    <div className={`custom-select ${cityOpen ? "open" : ""}`}>
                        <div
                            className="select-btn"
                            onClick={() => availableCities.length > 0 && setCityOpen(!cityOpen)}
                        >
                            {currentCity()}
                        </div>
                        {availableCities.length > 0 && (
                            <div className="options">
                                {availableCities.map(city => (
                                    <div
                                        key={city.id}
                                        className={`option${city.id === currentProduct?.region?.id ? " selected" : ""}`}
                                        onClick={() => {
                                            setCityOpen(false);
                                            // #region agent log
                                            fetch('http://127.0.0.1:7591/ingest/d2880673-f438-4573-9075-68f79f8bcef7',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'da59d4'},body:JSON.stringify({sessionId:'da59d4',runId:'city-hex',hypothesisId:'H-CITY',location:'MenuPanel.jsx:city-click',message:'select region of same product',data:{productName:currentProductName(),fromRegion:currentProduct?.region?.name,toRegion:city.name,toIndex:city.productIndex,cityCount:availableCities.length,cities:availableCities.map(c=>c.name)},timestamp:Date.now()})}).catch(()=>{});
                                            // #endregion
                                            setCurrentProductIndex(city.productIndex);
                                        }}
                                    >
                                        {city.name}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Слои и подслои */}
                    <div className="section-title">{t.layers.title}</div>
                    <div className="tile-wrapper">
                        {currentProduct.layers?.length > 0 ? (
                            currentProduct.layers.map(layer => {
                                const isOpen = openLayer === layer.id;
                                const showScroll = isOpen && layer.features?.length > 4;

                                return (
                                    <div key={layer.id}>
                                        <div
                                            className={`tile ${selectedLayerId === layer.id ? "selected" : ""}`}
                                            onClick={() => {
                                                setOpenLayer(prev => prev === layer.id ? null : layer.id);
                                                handleSelectionLayer(layer.id);
                                            }}
                                        >
                                            {layer.full_name}
                                        </div>

                                        {isOpen && layer.features?.length > 0 && (
                                            <div
                                                className={`sub-layer-container`}
                                                style={{
                                                    maxHeight: showScroll ? "200px" : "auto",
                                                    overflowY: showScroll ? "auto" : "visible",
                                                }}
                                            >
                                                {layer.features.map(feature => (
                                                    <div
                                                        key={feature.id}
                                                        className={`sub-layer ${selectedFeatureId === feature.id ? "selected" : ""}`}
                                                        onClick={() => handleSelectionFeature(feature.id)}
                                                    >
                                                        {feature.located_name || feature.full_name}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )
                            })
                        ) : (
                            <div className="no-data-message">{t.layers.noLayers}</div>
                        )}
                    </div>

                    {/* Слайдер выбора года */}
                    <div className="slider-container">
                        <div className="section-title">{t.time.year.title}</div>
                        {availableYears.length === 0 ? (
                            <div className="product-label">{t.time.year.noData}</div>
                        ) : availableYears.length === 1 ? (
                            <>
                                <input
                                    type="range"
                                    min={0}
                                    max={0}
                                    value={0}
                                    className="slider"
                                    disabled
                                    style={{ opacity: 0.5, cursor: "not-allowed" }}
                                />
                                <div className="product-label">{availableYears[0]}</div>
                            </>
                        ) : (
                            <>
                                <input
                                    type="range"
                                    min={0}
                                    max={availableYears.length - 1}
                                    step="1"
                                    value={Math.max(0, availableYears.indexOf(year))}
                                    className="slider"
                                    onChange={e => setYear(availableYears[Number(e.target.value)])}
                                />
                                <div className="product-label">{year}</div>
                            </>
                        )}
                    </div>

                    {/* Слайдер выбора месяца */}
                    <div className="slider-container">
                        <div className="section-title">{t.time.month.title}</div>
                        {availableMonths.length === 0 ? (
                            <div className="product-label">{t.time.month.noData}</div>
                        ) : availableMonths.length === 1 ? (
                            <>
                                <input
                                    type="range"
                                    min={1}
                                    max={1}
                                    value={1}
                                    className="slider"
                                    disabled
                                    style={{ opacity: 0.5, cursor: "not-allowed" }}
                                />
                                <div className="product-label">
                                    {t.months[availableMonths[0] - 1]}
                                </div>
                            </>
                        ) : (
                            <>
                                <input
                                    type="range"
                                    min={1}
                                    max={availableMonths.length}
                                    step={1}
                                    value={availableMonths.indexOf(month) + 1}
                                    className="slider"
                                    onChange={e => setMonth(availableMonths[Number(e.target.value) - 1])}
                                />
                                <div className="product-label">
                                    {t.months[month - 1]}
                                </div>
                            </>
                        )}
                    </div>

                    {/* === НОВЫЙ БЛОК: ВЫБОР РАЗМЕРА ГЕКСАГОНА === */}
                    <HexSizeSwitcher
                        regionId={currentProduct?.region?.id}
                        hexSize={hexSize}
                        setHexSize={setHexSize}
                    />
                    {/* =========================================== */}
                </div>

                {/* Вкладка выбранной точки */}
                <div className={`tab-content ${activeTab === "point" ? "active" : ""}`}>
                    <div className="selected-point-panel">
                        {/* ---- НОВЫЙ БЛОК ДЛЯ КЛАСТЕРОВ ---- */}
                        {clusterData.length > 0 && (
                            <>
                                <div className="cluster-tabs">
                                    {clusterData.map((item, idx) => {
                                        const featureId = selectedFeatureId ||
                                            currentProduct.layers?.find(l => l.id === selectedLayerId)?.features?.[0]?.id;
                                        const value = featureId ? (item.values?.[String(featureId)] ?? null) : null;
                                        const displayValue = value != null ? formatValue(value) : '—';
                                        const count = item.cluster.length;

                                        return (
                                            <button
                                                key={idx}
                                                className={`cluster-tab ${idx === activeClusterIdx ? 'active' : ''}`}
                                                onClick={() => {
                                                    setActiveClusterIdx(idx);
                                                    if (onClusterFlash) {
                                                        onClusterFlash(item.cluster);
                                                    }
                                                }}
                                            >
                                                {`${t.selectedPoint.area} ${idx + 1} (${count} ${declOfNum(count, t.selectedPoint.hexCount)})`}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Данные активного кластера */}
                                <div className="cluster-data-grid">
                                    {currentProduct.layers
                                        ?.find(layer => layer.id === selectedLayerId)
                                        ?.features.map(feature => {
                                            const val = clusterData[activeClusterIdx]?.values?.[String(feature.id)];
                                            return (
                                                <div key={feature.id} className="data-rectangle">
                                                    <div className="data-rectangle-left">
                                                        {feature.located_name || feature.full_name}
                                                    </div>
                                                    <div className="data-rectangle-right">
                                                        {val != null ? formatValue(val) : t.common.noData}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </div>

                                {/* Общая агрегация по всем кластерам (сумма) */}
                                {pickedAreaData && Object.keys(pickedAreaData).length > 0 && (
                                    <div className="total-aggregation">
                                        <div className="aggregation-info">
                                            {t.selectedPoint.aggregated}
                                        </div>
                                        {currentProduct.layers
                                            ?.find(layer => layer.id === selectedLayerId)
                                            ?.features.map(feature => {
                                                const val = pickedAreaData[String(feature.id)];
                                                return (
                                                    <div key={feature.id} className="data-rectangle total-rectangle">
                                                        <div className="data-rectangle-left">
                                                            {feature.located_name || feature.full_name}
                                                        </div>
                                                        <div className="data-rectangle-right">
                                                            {val != null ? formatValue(val) : t.common.noData}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                )}
                            </>
                        )}

                        {clusterData.length === 0 && (
                            <>
                                {(!selectedObjectValue && (!selectedObjectIds || selectedObjectIds.length === 0)) && (
                                    <div className="no-data">{t.selectedPoint.noSelection}</div>
                                )}

                                {(selectedObjectIds?.length > 0 && isLoading) && (
                                    <div className="no-data">{t.selectedPoint.loading}</div>
                                )}

                                {selectedObjectValue && pickedAreaData && Object.keys(pickedAreaData).length > 0 && (
                                    <div className="point-data-grid">
                                        {currentProduct.layers
                                            ?.find(layer => layer.id === selectedLayerId)
                                            ?.features.map(feature => {
                                                const val = pickedAreaData[String(feature.id)];
                                                return (
                                                    <div key={feature.id} className="data-rectangle">
                                                        <div className="data-rectangle-left">
                                                            {feature.located_name || feature.full_name}
                                                        </div>
                                                        <div className="data-rectangle-right">
                                                            {val != null ? formatValue(val) : t.common.noData}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                )}

                                {!selectedObjectValue && selectedObjectIds?.length > 1 && !isLoading && pickedAreaData && Object.keys(pickedAreaData).length > 0 && (
                                    <div className="point-data-grid">
                                        <div className="aggregation-info">
                                            {t.selectedPoint.aggregated}
                                        </div>
                                        {currentProduct.layers
                                            ?.find(layer => layer.id === selectedLayerId)
                                            ?.features.map(feature => {
                                                const val = pickedAreaData[String(feature.id)];
                                                return (
                                                    <div key={feature.id} className="data-rectangle">
                                                        <div className="data-rectangle-left">
                                                            {feature.located_name || feature.full_name}
                                                        </div>
                                                        <div className="data-rectangle-right">
                                                            {val != null ? formatValue(val) : t.common.noData}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                )}

                                {!selectedObjectValue && selectedObjectIds?.length > 0 && !isLoading && (!pickedAreaData || Object.keys(pickedAreaData).length === 0) && (
                                    <div className="no-data">{t.selectedPoint.noData}</div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

